import type { Chart } from "../Chart";
import { zoneTag, LABEL_COLORS } from "../indicators/DSzone";

// Paint DSzone highlights (behind candles) + pattern labels.
export function drawDSZones(ctx: CanvasRenderingContext2D, chart: Chart) {
  const eng = chart.dszEngine;
  const data = chart.data;
  if (!eng || !data) return;
  const vp = chart.viewport;
  const [lo, hi] = vp.visibleRange();
  const spacing = vp.barSpacing;
  const scanHi = Math.min(chart.effBarCount, hi + eng.maxSpan);

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, vp.plot.width, vp.plot.height);
  ctx.clip();

  // background zones
  for (let k = lo; k < scanHi; k++) {
    const res = eng.at(k);
    if (!res) continue;
    const xL = vp.xOfIndex(k - res.span + 1);
    const xR = vp.xOfIndex(k + 1);
    ctx.fillStyle = res.color;
    ctx.fillRect(xL, 0, xR - xL, vp.plot.height);
  }

  // labels (skip when bars are too dense to read)
  if (spacing >= 4) {
    ctx.font = "10px -apple-system, system-ui, sans-serif";
    for (let k = lo; k < scanHi; k++) {
      const res = eng.at(k);
      if (!res) continue;
      let upSlot = 0;
      let dnSlot = 0;
      for (const t of res.types) {
        const demand = t.name === "DBR" || t.name === "RBR";
        const x = vp.xCenterOfIndex(k);
        if (demand) {
          const y = vp.yOfPrice(data.highAt(k)) - 6 - upSlot * 18;
          drawLabel(ctx, x, y, zoneTag(t), LABEL_COLORS[t.name], false);
          upSlot++;
        } else {
          const y = vp.yOfPrice(data.lowAt(k)) + 6 + dnSlot * 18;
          drawLabel(ctx, x, y, zoneTag(t), LABEL_COLORS[t.name], true);
          dnSlot++;
        }
      }
    }
    // entry-filter labels (✓ entry / ✗ fast / ✗ straight) on the candle that touched the zone's entry
    const shown = new Set<string>();
    const lastBar = chart.effBarCount - 1;
    for (let k = Math.max(0, lo - 500); k < Math.min(hi, lastBar); k++) {
      const res = eng.at(k);
      if (!res) continue;
      for (const t of res.types) {
        const ev = eng.entryEvent(k, t);
        if (!ev || ev.bar < lo || ev.bar >= hi || ev.bar > lastBar) continue;
        const key = `${ev.bar}:${ev.demand}:${ev.text}`;
        if (shown.has(key)) continue;   // DBR + RBR on one candle share an entry → one label
        shown.add(key);
        const x = vp.xCenterOfIndex(ev.bar);
        const bg = ev.text === "✓ entry" ? "rgba(0,137,123,0.9)" : "rgba(120,123,134,0.9)";
        if (ev.demand) drawLabel(ctx, x, vp.yOfPrice(data.lowAt(ev.bar)) + 6, ev.text, bg, true);
        else drawLabel(ctx, x, vp.yOfPrice(data.highAt(ev.bar)) - 6, ev.text, bg, false);
      }
    }
  }
  ctx.restore();
}

function drawLabel(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, bg: string, below: boolean) {
  const padX = 5;
  const h = 15;
  const w = ctx.measureText(text).width + padX * 2;
  const bx = x - w / 2;
  const by = below ? y : y - h;
  ctx.fillStyle = bg;
  ctx.beginPath();
  const r = 3;
  ctx.moveTo(bx + r, by);
  ctx.arcTo(bx + w, by, bx + w, by + h, r);
  ctx.arcTo(bx + w, by + h, bx, by + h, r);
  ctx.arcTo(bx, by + h, bx, by, r);
  ctx.arcTo(bx, by, bx + w, by, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, by + h / 2 + 0.5);
}
